import { createContext, useCallback, useContext, useLayoutEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface Claim {
	/** The side panel's accessible name */
	label: string;
}

interface Holder {
	host: HTMLElement | null;
	claim: (claim: Claim | null) => void;
}

const Context = createContext<Holder | null>(null);

/**
 * What the page in view asked of the shell's side panel, and where its content goes: the shell's `Page` draws a raised
 * panel beside the main column only while a page claims one (`Aside`), and hands this the element that holds it
 */
export function useAsideHost() {
	const [claim, setClaim] = useState<Claim | null>(null);
	const [host, setHost] = useState<HTMLElement | null>(null);
	const holder: Holder = { host, claim: setClaim };
	const place = useCallback((element: HTMLElement | null) => setHost(element), []);
	return { claim, holder, place };
}

/** Gives the pages below it the shell's side panel */
export function AsideProvider({ holder, children }: { holder: Holder; children: ReactNode }) {
	return <Context.Provider value={holder}>{children}</Context.Provider>;
}

/**
 * A resource page's conditions (where it lives, its settings, its access) in the shell's raised side panel, beside
 * the work that leads its main column (the family's resource page, `Page`'s `raised`, `@beyond-js/ui` 0.30.0). It is
 * claimed before the page is painted, so the panel never appears after what is drawn (D185); below the width where it
 * sits beside the main column, the family's `Page` puts it after that column.
 */
export function Aside({ label, children }: { label: string; children: ReactNode }) {
	const holder = useContext(Context);
	const claim = holder?.claim;
	useLayoutEffect(() => {
		claim?.({ label });
		return () => claim?.(null);
	}, [claim, label]);
	// Outside a shell that holds a side panel (a page drawn on its own) the conditions stay in the page's column
	if (!holder) return <>{children}</>;
	return holder.host ? createPortal(children, holder.host) : null;
}
